<script setup lang="ts">
import Logo from "@/components/auth/Logo.vue";
import Menu, { Tab } from "@/components/auth/Menu.vue";
import type { RouteRecordNameGeneric } from "vue-router";
import { useRoute, useRouter } from "vue-router";
import { createLoginLink, createRegistrationLink } from "@/router/linkFactories";
import { RouteName } from "@/router/routeNameEnum";
import { whenRefAvailable } from "@/utilities/component";
import { onMounted } from "vue";

const Topbar = useAsyncComponent(() => import("@/components/layouts/shared/topbar/Topbar.vue"));
const Footer = useAsyncComponent(() => import("@/components/layouts/shared/FooterFull.vue"));
const Dialog = useAsyncComponent(() => import("@/components/basics/Dialog.vue"));

const dialog = ref<InstanceType<typeof Dialog> | null>(null);
onMounted(() => whenRefAvailable(dialog).then(() => dialog.value?.open()));

const route = useRoute();
const router = useRouter();
const showTabs = computed(() => {
  const showTabsRoutes: RouteRecordNameGeneric[] = [RouteName.Registration, RouteName.Login];
  return showTabsRoutes.includes(route.name);
});
const currentTab = computed(() => (route.name === RouteName.Registration ? Tab.Registration : Tab.Login));
</script>

<template>
  <div id="top" class="locking-state-layout">
    <Topbar />

    <main class="locking-state-layout__content">
      <Dialog ref="dialog" :show-close="false" :close-on-click-modal="false" :close-on-press-escape="false" class="locked-dialog">
        <Logo />
        <Menu v-if="showTabs" :default-active-tab="currentTab" @login-click="router.push(createLoginLink())" @registration-click="router.push(createRegistrationLink())" />
        <RouterView name="mainContent" />
      </Dialog>
    </main>

    <Footer />
  </div>
</template>

<style lang="scss" scoped>
.locking-state-layout {
  &__content {
    min-height: 70vh;
  }
}
</style>

<style lang="scss">
// Unscoped, matching Dialog.vue's own convention: ElDialog (and its teleported root) can only be
// reliably targeted by class, not by a scoped descendant selector.
.locked-dialog {
  --dialogWidth: min(450px, 90vw);
  @include mixins.maxScreenWidth(sm) {
    --dialogWidth: 100dvw;
  }
}
</style>
